import { useState } from "react";
import moment from "moment-timezone";
import { FaFilePdf, FaDownload } from "react-icons/fa";
import { useGetDevicesStatusBetweenDatesPdfReportMutation } from "../../../store/storageRoles/reportsApi";
import useAuth from "../../../Hooks/useAuth";
import ReportLayout from "../../../Layout/Reports/ReportLayout";
import ReportFilterCard from "../../../Components/Reports/ReportFilterCard";
import ReportLoadingState from "../../../Components/Reports/ReportLoadingState";
import ReportErrorState from "../../../Components/Reports/ReportErrorState";
import ReportEmptyState from "../../../Components/Reports/ReportEmptyState";
import ReportPdfPreview, {
  downloadPdfFromBase64,
} from "../../../Components/Reports/ReportPdfPreview";
import { TextInput } from "../../../Components/atoms";
import { getMonthStart } from "../../../utils/reportDateDefaults";

/**
 * CİHAZ DURUM RAPORU — Excel/tablo yerine PDF.
 *
 * Rapor artık sunucuda PDF olarak üretilir (bkz.
 * `report-device-pdf.controller.js` → `device-status.aggregator.js` →
 * `device-status-report-pdf-html-generator.js`). Sayfa yalnızca filtreyi
 * toplayıp base64 PDF'i önizler ve indirtir.
 *
 * VERİ KAYNAĞI DEĞİŞMEDİ: sunucu aynı aggregation'ı çalıştırıyor
 * (`deviceupdateinfologs` → `infoName: "Konum"` kayıtları), filtreler de aynı
 * ({ startDate, endDate, serialNo }).
 *
 * Yerleşim `FirmPerformanceUnifiedReportPage` desenidir: `showExcel={false}`
 * + başlıkta PDF indirme butonu + solda filtre kartı + sağda PDF önizleme.
 * Tarih seçici `ReportFilterCard`'ın kendi `showDatePickers` alanıdır —
 * eski sayfadaki ayrı `LocalizationProvider` sarmalayıcısına gerek yok,
 * `ReportLayout` zaten tüm sayfayı sarıyor.
 */
export default function ReportDevicesStatusBetweenDatesPage() {
  const { auth } = useAuth();
  const token = auth.accessToken;

  // Gün sınırları kullanıcının kendi saat diliminde alınır — eski davranış
  // birebir korundu; sunucu gelen ISO damgalarını olduğu gibi kullanıyor.
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const getEndOfDay = (date) => moment(date).tz(tz).endOf("day").toDate();

  // Başlangıç varsayılanı ayın ilk günü (Workspace #154)
  const [startDate, setStartDate] = useState(() => getMonthStart(tz));
  const [endDate, setEndDate] = useState(getEndOfDay(new Date()));
  const [serialNo, setSerialNo] = useState("");

  const [getReport, { data, isLoading, error, reset }] =
    useGetDevicesStatusBetweenDatesPdfReportMutation();

  const canSearch = Boolean(startDate && endDate);

  const handleSearch = () => {
    if (!canSearch) return;
    getReport({ token, startDate, endDate, serialNo });
  };

  const handleClearFilters = () => {
    setStartDate(getMonthStart(tz));
    setEndDate(getEndOfDay(new Date()));
    setSerialNo("");
    reset?.();
  };

  const report = data?.data;
  const pdfBase64 = report?.pdfBase64;

  const errorMessage =
    error?.data?.message || error?.error || "Rapor oluşturulurken bir hata oluştu.";

  const headerActions = pdfBase64 ? (
    <button
      onClick={() => downloadPdfFromBase64(pdfBase64, report?.fileName)}
      title="PDF İndir"
      className="flex items-center gap-1.5 px-2.5 py-2 sm:px-3.5 bg-red-600 text-white rounded-lg shadow-sm hover:bg-red-700 active:bg-red-800 transition-colors text-xs font-SemiBold"
    >
      <FaFilePdf className="w-4 h-4" />
      <FaDownload className="w-3 h-3" />
      <span className="hidden sm:inline">PDF İndir</span>
    </button>
  ) : null;

  const activeFilters = [
    ...(startDate && endDate
      ? [
          {
            label: "Dönem",
            value: `${startDate.toLocaleDateString(
              "tr-TR",
            )} - ${endDate.toLocaleDateString("tr-TR")}`,
          },
        ]
      : []),
    ...(serialNo ? [{ label: "Seri No", value: serialNo }] : []),
  ];

  return (
    <ReportLayout
      title="Cihaz Durum Raporu"
      showExcel={false}
      headerActions={headerActions}
      sidebar={
        <ReportFilterCard
          title="Cihaz Durum Filtreleme"
          description="Rapor için tarih aralığını ve isterseniz seri numarasını seçin"
          showDatePickers
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          onSearch={handleSearch}
          onClear={handleClearFilters}
          isLoading={isLoading}
          searchDisabled={!canSearch}
          searchDisabledMessage="Başlangıç ve bitiş tarihi zorunludur"
          searchButtonText="Raporu Getir"
          clearButtonText="Temizle"
          activeFilters={activeFilters}
        >
          <div className="grid grid-cols-1 gap-4">
            <TextInput
              label="Seri No"
              placeholder="Seri numarası girin..."
              value={serialNo}
              onChange={(e) => setSerialNo(e.target.value)}
            />
          </div>
        </ReportFilterCard>
      }
    >
      {isLoading && (
        <ReportLoadingState
          title="Cihaz Durum Raporu Hazırlanıyor"
          description="PDF üretiliyor, lütfen bekleyin..."
          progressText="Veriler işleniyor..."
          skeletonRows={6}
          skeletonColumns={5}
        />
      )}

      {!isLoading && error && (
        <ReportErrorState title="Rapor Yüklenemedi" message={errorMessage} icon="warning" />
      )}

      {!isLoading && !error && pdfBase64 && (
        <ReportPdfPreview
          pdfBase64={pdfBase64}
          title={`${report?.dateRange || "Dönem"} — ${report?.recordCount ?? 0} kayıt`}
        />
      )}

      {!isLoading && !error && !pdfBase64 && (
        <ReportEmptyState
          title="Rapor Bekleniyor"
          message="Tarih aralığını seçip 'Raporu Getir' butonuna basın."
          icon="document"
        />
      )}
    </ReportLayout>
  );
}
